import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProjectMap } from '@/widgets/project-map'
import { useSendMessage, useSetup, useSetupChat, setupApi, markOnboardingSkipped } from '@/entities/setup'
import { Dictation, transcribe } from '@/features/dictation/lib/recorder'
import { CallSession, microphoneAvailable } from '@/features/test-call/lib/session'
import { useCallStore } from '@/features/test-call/model'
import { CallStage } from '@/features/test-call/ui/CallStage'
import { callsApi, useCall } from '@/entities/call'
import { ReportCard } from '@/entities/call/ui/ReportCard'
import { toast } from '@/shared/model/toast'
import type { Applied } from '@/shared/api/types'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import styles from './ui/OnboardingPage.module.css'

/**
 * Настройка агента разговором.
 *
 * Владелец бизнеса не заполняет анкету из пятнадцати полей — он
 * рассказывает, чем занимается. Настройщик задаёт вопросы и
 * раскладывает сказанное по полям сам; человек видит это в карте
 * справа по ходу разговора.
 */
export function OnboardingPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const setup = useSetup()
  const chat = useSetupChat()
  const send = useSendMessage()

  const [text, setText] = useState('')
  const [applied, setApplied] = useState<Applied[]>([])
  const [options, setOptions] = useState<string[]>([])
  // Счётчик ходов: карта по нему перезапускает подсветку шага, даже если
  // два хода подряд закрыли один и тот же шаг.
  const [tick, setTick] = useState(0)
  const feedRef = useRef<HTMLDivElement>(null)

  const call = useTestCall()

  const dictation = useDictation((recognized) => {
    // Текст попадает в поле, а не уходит сразу: распознавание ошибается
    // на именах и числах, и последнее слово за человеком.
    setText((prev) => (prev ? `${prev} ${recognized}` : recognized))
  })

  // Первый ход делает настройщик: пустой экран с курсором ничего не
  // объясняет, а вопрос — объясняет.
  useEffect(() => {
    if (chat.isPending || chat.data?.length || send.isPending) return
    send.mutate('', { onSuccess: (reply) => setOptions(reply.options ?? []) })
    // Один раз при пустой истории: зависимость от send.mutate дала бы
    // повторный вызов на каждый рендер.
  }, [chat.isPending, chat.data?.length])

  useEffect(() => {
    const feed = feedRef.current
    if (feed) feed.scrollTop = feed.scrollHeight
  }, [chat.data, send.isPending, applied, options])

  function sendText(message: string) {
    const trimmed = message.trim()
    if (!trimmed || send.isPending) return

    // Варианты и карточки прошлого хода убираются сразу: они отвечали
    // на вопрос, которого на экране уже нет.
    setOptions([])
    setApplied([])

    send.mutate(trimmed, {
      onSuccess: (reply) => {
        setApplied(reply.applied ?? [])
        setOptions(reply.options ?? [])
        setTick((t) => t + 1)
      },
      onError: () => {
        toast.error('Настройщик не ответил. Попробуйте ещё раз')
        setText(trimmed)
      },
    })
  }

  function submit() {
    const message = text
    setText('')
    sendText(message)
  }

  async function reset() {
    await setupApi.reset()
    setApplied([])
    setOptions([])
    void queryClient.invalidateQueries({ queryKey: queryKeys.setupChat })
  }

  // В кабинет — по своему решению, а не только когда всё готово.
  // Флаг снимает замок RequireSetup: иначе гвард вернул бы обратно.
  function enterCabinet() {
    markOnboardingSkipped()
    navigate('/', { replace: true })
  }

  return (
    <div className={styles.screen}>
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <LogoIcon />
          </span>
          <span>
            синта<span className={styles.brandSoft}>.бот</span>
          </span>
        </div>

        <span className={styles.topTitle}>Настройка агента</span>
        <button type="button" className={styles.skip} onClick={() => navigate('/account')}>Пароль</button>

        {setup.data?.ready ? (
          <button type="button" className={styles.toCabinet} onClick={enterCabinet}>
            В кабинет →
          </button>
        ) : (
          <button type="button" className={styles.skip} onClick={enterCabinet}>
            настроить позже
          </button>
        )}
      </header>

      <div className={styles.body}>
      {setup.data?.ready && !call.live && (
        <div className={styles.finale}>
          <div className={styles.finaleGlow} aria-hidden="true" />
          <div className={styles.finalePhone}>
            <span className={styles.finaleRing} aria-hidden="true" />
            <span className={`${styles.finaleRing} ${styles.finaleRingLate}`} aria-hidden="true" />
            <PhoneIcon />
          </div>
          <div className={styles.finaleText}>
            <div className={styles.finaleLabel}>
              {call.finishedId ? 'разговор закончился' : 'всё настроено'}
            </div>
            <div className={styles.finaleTitle}>
              {call.finishedId ? 'Как вам агент?' : 'Протестируйте своего агента'}
            </div>
            <div className={styles.finaleHint}>
              {call.finishedId
                ? 'Что-то не так — скажите настройщику, он поправит. Потом позвоните ещё раз.'
                : 'Поговорите с ним как ваш клиент. Разговор пойдёт прямо на этой странице.'}
            </div>
          </div>
          <button
            type="button"
            className={styles.finaleButton}
            onClick={call.start}
            disabled={call.starting || !microphoneAvailable()}
          >
            <PhoneIcon />
            {call.finishedId ? 'Позвонить ещё раз' : 'Позвонить'}
          </button>
        </div>
      )}

      <div className={`${styles.layout} ${call.live ? styles.layoutCalling : ''}`}>
        {call.live && (
          <div className={styles.call}>
            <div className={styles.callHead}>
              <span className={styles.chatTitle}>Разговор с агентом</span>
              <span className={styles.chatHint}>говорите как ваш клиент</span>
            </div>
            <div className={styles.callBody}>
              {call.assembling ? (
                <Assembly setup={setup.data} />
              ) : (
                <CallStage onHangUp={call.stop} />
              )}
            </div>
          </div>
        )}

        <div className={styles.chat}>
          <div className={styles.chatHead}>
            <span className={styles.chatTitle}>Настройщик</span>
            <span className={styles.chatHint}>рассказывайте обычными словами</span>
            {(chat.data?.length ?? 0) > 0 && (
              <button type="button" className={styles.reset} onClick={reset}>
                начать заново
              </button>
            )}
          </div>

          <div className={styles.feed} ref={feedRef}>
            {chat.data?.map((message) => (
              <div
                key={message.id}
                className={`${styles.row} ${message.role === 'user' ? styles.rowUser : ''}`}
              >
                {message.role === 'assistant' && (
                  <span className={styles.avatar}>
                    <LogoIcon />
                  </span>
                )}
                <div
                  className={`${styles.bubble} ${
                    message.role === 'user' ? styles.user : styles.assistant
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}

            {applied.length > 0 && !send.isPending && (
              <div className={styles.cards}>
                {applied.map((item, i) => (
                  <div
                    key={`${tick}-${i}`}
                    className={styles.card}
                    style={{ animationDelay: `${i * 110}ms` }}
                  >
                    <span
                      className={`${styles.cardIcon} ${
                        item.kind === 'remove' ? styles.cardIconRemove : ''
                      }`}
                    >
                      <CardGlyph kind={item.kind} />
                    </span>
                    <span className={styles.cardBody}>
                      <span className={styles.cardTitle}>{item.title}</span>
                      {item.value && <span className={styles.cardValue}>{item.value}</span>}
                    </span>
                    <span className={styles.cardMark} style={{ animationDelay: `${i * 110 + 260}ms` }}>
                      <CheckIcon />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {options.length > 0 && !send.isPending && (
              <div className={styles.options}>
                {options.map((option, i) => (
                  <button
                    key={`${tick}-${option}`}
                    type="button"
                    className={styles.option}
                    style={{ animationDelay: `${i * 70}ms` }}
                    onClick={() => sendText(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {send.isPending && (
              <div className={styles.row}>
                <span className={styles.avatar}>
                  <LogoIcon />
                </span>
                <div className={`${styles.bubble} ${styles.assistant}`}>
                  <span className={styles.typing}>
                    <span className={styles.typingDot} />
                    <span className={styles.typingDot} />
                    <span className={styles.typingDot} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.composer}>
            <div className={styles.field}>
              <textarea
                className={styles.input}
                placeholder="Расскажите, чем занимается ваша компания…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  // Enter отправляет, Shift+Enter переносит строку: это
                  // чат, а не форма, и переносы здесь нужны редко.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    submit()
                  }
                }}
                rows={1}
              />

              <button
                type="button"
                className={`${styles.mic} ${dictation.recording ? styles.micActive : ''}`}
                onClick={dictation.toggle}
                disabled={dictation.busy}
                aria-label={dictation.recording ? 'Остановить запись' : 'Надиктовать'}
                style={
                  dictation.recording
                    ? { transform: `scale(${1 + Math.min(dictation.level * 1.2, 0.25)})` }
                    : undefined
                }
              >
                <MicIcon />
              </button>

              <button
                type="button"
                className={styles.send}
                onClick={submit}
                disabled={!text.trim() || send.isPending}
                aria-label="Отправить"
              >
                <SendIcon />
              </button>
            </div>

            <div className={styles.hint}>
              {dictation.recording ? (
                <span className={styles.recording}>
                  <span className={styles.recordingDot} />
                  идёт запись — нажмите ещё раз, чтобы закончить
                </span>
              ) : dictation.busy ? (
                <span>распознаём…</span>
              ) : (
                <>
                  <span>
                    <span className={styles.key}>Enter</span> отправить
                  </span>
                  <span>
                    <span className={styles.key}>Shift+Enter</span> перенос строки
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {call.live ? null : (
          <ProjectMap
            setup={setup.data}
            flash={applied.map((item) => item.step ?? '').filter(Boolean)}
            tick={tick}
          />
        )}
      </div>

      {call.finishedId && !call.live && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <CallResult callId={call.finishedId} />
        </div>
      )}
      </div>
    </div>
  )
}

/**
 * Тестовый звонок прямо на странице настройки.
 *
 * Не переходом на другой экран: правку хочется сказать сразу, пока
 * помнишь, что не понравилось, — а для этого настройщик должен
 * оставаться рядом.
 */
function useTestCall() {
  const [live, setLive] = useState(false)
  const [starting, setStarting] = useState(false)
  const [assembling, setAssembling] = useState(false)
  const [finishedId, setFinishedId] = useState<number | null>(null)
  const session = useRef<CallSession | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    return () => {
      session.current?.stop()
      session.current = null
    }
  }, [])

  async function start() {
    setStarting(true)
    setFinishedId(null)

    // Через getState, а не через хук: micLevel в этом сторе меняется
    // каждый кадр, и подписка на него перерисовывала бы заодно чат с
    // картой проекта — шестьдесят раз в секунду ради полоски громкости.
    const store = useCallStore.getState()

    try {
      const call = await callsApi.startTest()
      store.begin(call.callId)
      setLive(true)
      // Сборка показывается, пока браузер спрашивает микрофон: пустой
      // экран в этот момент выглядит зависанием.
      setAssembling(true)
      window.setTimeout(() => setAssembling(false), 1600)

      const created = new CallSession({
        onEvent: store.handle,
        onLevel: store.setLevel,
        onStatus: (next, detail) => {
          store.setStatus(next, detail)
          if (next === 'ended' || next === 'error') {
            setLive(false)
            setAssembling(false)
            setFinishedId(call.callId)
            void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
          }
        },
      })

      session.current = created
      await created.start(call.mediaUrl, call.eventsUrl)
    } catch {
      toast.error('Не удалось начать звонок')
      setLive(false)
      setAssembling(false)
    } finally {
      setStarting(false)
    }
  }

  function stop() {
    session.current?.stop()
    session.current = null
  }

  return { live, starting, assembling, finishedId, start, stop }
}

/** Из чего собрался агент. Показывается перед первым звуком. */
function Assembly({ setup }: { setup?: import('@/shared/api/types').Setup }) {
  const rows = [
    `Промпт компании «${setup?.profile.companyLegal || 'без названия'}»`,
    `${setup?.openers.length ?? 0} стартовых фраз`,
    `${setup?.stages.length ?? 0} этапов разговора`,
    'Голос и телефонная линия',
  ]

  return (
    <div className={styles.assembly}>
      {rows.map((row, i) => (
        <div key={row} className={styles.assemblyRow} style={{ animationDelay: `${i * 260}ms` }}>
          <span className={styles.assemblyMark}>
            <CheckIcon />
          </span>
          {row}
        </div>
      ))}
    </div>
  )
}

/** Отчёт по только что закончившемуся разговору. */
function CallResult({ callId }: { callId: number }) {
  const call = useCall(callId)
  if (!call.data) return null

  return (
    <div className={styles.chat} style={{ height: 'auto' }}>
      <div className={styles.chatHead}>
        <span className={styles.chatTitle}>Чем закончился разговор</span>
      </div>
      <div style={{ padding: 'var(--space-5)' }}>
        <ReportCard call={call.data.call} />
      </div>
    </div>
  )
}

/**
 * Диктовка: нажали — пишет, нажали ещё раз — расшифровывает.
 *
 * Не «удерживать для записи»: рассказ про бизнес длится минуту, и
 * держать кнопку всё это время неудобно.
 */
function useDictation(onText: (text: string) => void) {
  const [recording, setRecording] = useState(false)
  const [busy, setBusy] = useState(false)
  const [level, setLevel] = useState(0)
  const recorder = useRef<Dictation | null>(null)

  // Микрофон нельзя оставлять включённым при уходе со страницы.
  useEffect(() => {
    return () => recorder.current?.cancel()
  }, [])

  async function toggle() {
    if (busy) return

    if (recording) {
      const blob = recorder.current?.stop()
      recorder.current = null
      setRecording(false)
      setLevel(0)

      if (!blob) return
      setBusy(true)
      try {
        const text = await transcribe(blob)
        if (text) onText(text)
        else toast.info('Ничего не расслышали — попробуйте ещё раз')
      } catch {
        toast.error('Не удалось распознать речь')
      } finally {
        setBusy(false)
      }
      return
    }

    const dictation = new Dictation(setLevel)
    try {
      await dictation.start()
      recorder.current = dictation
      setRecording(true)
    } catch {
      toast.error('Браузер не дал доступ к микрофону')
    }
  }

  return { recording, busy, level, toggle }
}

/** Значок карточки изменения: по виду понятно, что записали. */
function CardGlyph({ kind }: { kind: Applied['kind'] }) {
  switch (kind) {
    case 'opener':
      return (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M2 9.5C2 6 4 3.5 6.5 2.5M8 9.5C8 6 10 3.5 12.5 2.5M2 9.5H5V12H2V9.5ZM8 9.5H11V12H8V9.5Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'stage':
      return (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M2 3H12M2 7H12M2 11H8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )
    case 'remove':
      return (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M3 7H11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      )
    default:
      return (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M9.5 2.5L11.5 4.5L5 11H3V9L9.5 2.5Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
  }
}

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.2 2.4L5.2 2.1C5.6 2.05 6 2.3 6.15 2.7L6.9 4.8C7.03 5.16 6.93 5.56 6.65 5.82L5.6 6.8C6.3 8.4 7.6 9.7 9.2 10.4L10.18 9.35C10.44 9.07 10.84 8.97 11.2 9.1L13.3 9.85C13.7 10 13.95 10.4 13.9 10.8L13.6 12.8C13.53 13.3 13.1 13.65 12.6 13.62C7.1 13.3 2.7 8.9 2.38 3.4C2.35 2.9 2.7 2.47 3.2 2.4Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function LogoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path
        d="M2 7.5H3.2M5.1 4.3V10.7M7.5 1.9V13.1M9.9 4.3V10.7M12.8 7.5H14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
      <path d="M1 4.2L3.6 6.6L9 1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MicIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5.6" y="1.8" width="4.8" height="7.6" rx="2.4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.4 7.6C3.4 10 5.5 11.8 8 11.8C10.5 11.8 12.6 10 12.6 7.6M8 11.8V14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 8H13M13 8L8.5 3.5M13 8L8.5 12.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
